import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { rotate } from 'components/utils/animations'
import useResizeObserver from 'hooks/useResizeObserver'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { fillArray } from 'lib/array/arrayUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import srOnly from 'styles/tools/sr-only'
import { CHAT_INPUT_INNER_RADIUS } from './ChatInputShell'
import { COMPOSER_LINE_HEIGHT } from './composerLayout'

interface Props {
  waveformLevels: Array<number>
  filledBars: number
  onCancel: () => void
  onSubmit: () => void
  isTranscribing?: boolean
  /** `stacked` shows the status beside the waveform, above the buttons, for a composer with a controls row */
  layout?: 'inline' | 'stacked'
}

const MIN_BAR_HEIGHT = 18
const MAX_BAR_HEIGHT = 100
const MIN_RENDER_BARS = 24
const MAX_RENDER_BARS = 320
const BAR_WIDTH = 3
const BAR_GAP = 2

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding-block: ${rem(12)};
  padding-inline: ${rem(18)} ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(CHAT_INPUT_INNER_RADIUS)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  z-index: ${zIndex.min};
  pointer-events: auto;

  &.stacked {
    flex-direction: column;
    align-items: flex-end;
    gap: ${rem(12)};
    padding: 0;
  }
`

const Status = styled.div`
  flex: 1;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  min-inline-size: 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
  }
`

const StatusText = styled(BodyText)`
  &.visually-hidden {
    ${srOnly}
  }
`

const Controls = styled.div`
  display: flex;
  gap: ${rem(8)};
`

const Waveform = styled.div`
  flex: 1;
  block-size: ${rem(36)};
  display: flex;
  align-items: center;
  gap: ${rem(BAR_GAP)};
  overflow: hidden;
  position: relative;

  &.single-line {
    block-size: ${rem(COMPOSER_LINE_HEIGHT)};
  }
`

const WaveformBar = styled.span`
  width: ${rem(BAR_WIDTH)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightForeground};
  opacity: 0.65;
  flex: 0 0 ${rem(BAR_WIDTH)};
  transform-origin: center;
  transition: height 0.12s ease;

  &.active {
    background: ${props => props.theme.palette.neutral.surface.darkForeground};
    opacity: 1;
  }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${rotate} 0.9s linear infinite;
`

function getBarCount(widthPx: number): number {
  // Bars are sized in rem; account for a customised root font size.
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize)
  const width = widthPx * 16 / rootFontSize
  const fittingBars = Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP))
  return Math.max(MIN_RENDER_BARS, Math.min(MAX_RENDER_BARS, fittingBars))
}

function RecordingOverlay({
  waveformLevels,
  filledBars,
  onCancel,
  onSubmit,
  isTranscribing = false,
  layout = 'inline',
}: Props) {
  const intl = useIntl()
  const listeningLabel = intl.formatMessage({
    id: 'yG0ipvfSjZkh6tmnmWxK',
    defaultMessage: 'Listening…',
    description: 'TripPlanner chat voice overlay status while the microphone is recording; announced to screen readers, and also shown beside the waveform on the landing page composer',
  })
  const transcribingLabel = intl.formatMessage({
    id: 'oRHr89Uqt_07yEV_tqwJ',
    defaultMessage: 'Transcribing…',
    description: 'TripPlanner chat voice overlay status while a recording is being turned into text; announced to screen readers, and also shown beside the waveform on the landing page composer',
  })
  const [renderedBarCount, setRenderedBarCount] = useState(MIN_RENDER_BARS)
  const waveformRef = useRef<HTMLDivElement>(null)

  const updateRenderedBars = useCallback((width: number) => {
    if (width) setRenderedBarCount(getBarCount(width))
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (waveformRef.current) updateRenderedBars(waveformRef.current.getBoundingClientRect().width)
  }, [updateRenderedBars])

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    updateRenderedBars(entry.contentRect.width)
  }, [updateRenderedBars])
  useResizeObserver(waveformRef, handleResize)

  const adjustedWaveformLevels = useMemo(() => {
    if (renderedBarCount === waveformLevels.length) return waveformLevels
    if (waveformLevels.length === 0) return []

    return fillArray(renderedBarCount, index => waveformLevels[Math.min(
      waveformLevels.length - 1,
      Math.floor((index / renderedBarCount) * waveformLevels.length),
    )])
  }, [renderedBarCount, waveformLevels])

  const firstActiveIndex = useMemo(() => {
    const activeCount = Math.min(
      renderedBarCount,
      Math.max(0, Math.round((filledBars / Math.max(1, waveformLevels.length)) * renderedBarCount)),
    )
    return renderedBarCount - activeCount
  }, [filledBars, renderedBarCount, waveformLevels.length])

  const waveformBars = useMemo(() => adjustedWaveformLevels.map((level, index) => {
    const height = Math.min(
      MAX_BAR_HEIGHT,
      Math.max(MIN_BAR_HEIGHT, MIN_BAR_HEIGHT + level * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT)),
    )
    // Inline: a styled prop would generate a class per height.
    return <WaveformBar key={`wave-${index}`} className={clsx({ active: index >= firstActiveIndex })} style={{ height: `${height}%` }} />
  }), [adjustedWaveformLevels, firstActiveIndex])

  const statusLabel = isTranscribing ? transcribingLabel : listeningLabel
  const isStacked = layout === 'stacked'

  return (
    <Overlay className={clsx({ stacked: isStacked })}>
      <Status>
        <StatusText
          role="status"
          variant="medium"
          colour="neutral-three"
          className={clsx({ 'visually-hidden': !isStacked })}
        >
          {statusLabel}
        </StatusText>
        <Waveform aria-hidden ref={waveformRef} className={clsx({ 'single-line': isStacked })}>
          {waveformBars}
        </Waveform>
      </Status>
      <Controls>
        <IconButton
          kind="secondary"
          shape="circle"
          size="medium"
          aria-label={intl.formatMessage({
            id: '42RE6uT3_lz0oE0LIAEA',
            defaultMessage: 'Cancel recording',
            description: 'Aria-label for the cancel recording button during voice input',
          })}
          onClick={onCancel}
        >
          <LineTimesIcon />
        </IconButton>
        <IconButton
          kind="primary"
          shape="circle"
          size="medium"
          aria-label={intl.formatMessage({
            id: '6lbT8mboL5lraJ8wKaPM',
            defaultMessage: 'Stop recording and transcribe',
            description: 'Aria-label for the stop recording and transcribe button during voice input',
          })}
          onClick={onSubmit}
          disabled={isTranscribing}
          autoFocus
        >
          {isTranscribing ? <SpinnerIcon /> : <LineCheckIcon />}
        </IconButton>
      </Controls>
    </Overlay>
  )
}

export default RecordingOverlay
